'use client';

import VoiceoverContainer, { VoiceoverContainerProps } from '@app/(darkHeader)/voiceover/VoiceoverContainer';
import {
    VoiceoverGenerationBridgeProvider,
    type VoiceoverGenerationBridgeValue,
} from '@app/(darkHeader)/voiceover/_context/VoiceoverGenerationBridge';
import { type JSX, useEffect, useMemo } from 'react';

import { AiToolkitToast } from '../../_shared/_components/Toast/AiToolkitToast';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import { AI_TOOLKIT_CATEGORIES } from '../../_shared/_context/types';

export type AIVoiceoverWrapperProps = VoiceoverContainerProps;

// Client component wrapper that bridges the shared voiceover UI to the AiToolkitContext: it disables
// the voiceover submit buttons while another AI Toolkit task is in progress, and surfaces that task's
// toast here on the voiceover page. Once Voiceover is fully migrated to the AI Toolkit, this can move
// into VoiceoverContainer.
export const AIVoiceoverWrapper = ({ active, basePath }: AIVoiceoverWrapperProps): JSX.Element => {
    const { activeTask, acknowledgeTask, submitStates } = useAiToolkitContext();
    const { status } = submitStates[AI_TOOLKIT_CATEGORIES.VOICEOVER];

    const showOtherTaskToast = activeTask !== null && activeTask.category !== 'voiceover';

    // NOTE: This is future facing since toast notifications for voiceover generations are not implemented.
    useEffect(() => {
        if (status === 'success' || status === 'error') {
            acknowledgeTask(AI_TOOLKIT_CATEGORIES.VOICEOVER, status);
        }
    }, [status, acknowledgeTask]);

    const bridge = useMemo<VoiceoverGenerationBridgeValue>(
        // Any in-progress AI Toolkit task disables the voiceover submit buttons.
        () => ({ submitDisabled: activeTask?.status === 'inProgress' }),
        [activeTask?.status],
    );

    return (
        <>
            <VoiceoverGenerationBridgeProvider value={bridge}>
                <VoiceoverContainer active={active} basePath={basePath} />
            </VoiceoverGenerationBridgeProvider>
            <AiToolkitToast open={showOtherTaskToast} activeTask={activeTask} />
        </>
    );
};
